import React, { useState, useEffect } from "react";
import { Button, Spinner } from "reactstrap";
import { useTranslation } from "react-i18next";
import { FlashMessage } from "../../Common/Helpers/FlashMessage";

// Decimal handling (same convention as NumberInput):
// - the user may type comma or dot, max 2 decimals
// - Swedish shows comma, other languages dot
// - values are always saved with dot (the backend parses them with en-GB culture)
const DECIMAL_INPUT = /^\d*([.,]\d{0,2})?$/;
const toApiDecimal = (v) => String(v ?? '').trim().replace(',', '.');

export const EconomicForm = ({ initial, onSave, onCancel, saving, settings }) => {
    const { t, i18n } = useTranslation();
    const isSwedish = (i18n.language || '').startsWith('se');
    const toDisplayDecimal = (v) => (v === '' || v === null || v === undefined)
        ? ''
        : (isSwedish ? String(v).replace('.', ',') : String(v));
    const [value, setValue] = useState(initial.value || '');
    const [taxes, setTaxes] = useState([]);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (initial.id === 'EmploymentTax') {
            try {
                const parsed = JSON.parse(initial.value || '[]');
                if (Array.isArray(parsed)) {
                    setTaxes(parsed);
                } else if (!isNaN(parseFloat(initial.value))) {
                    setTaxes([{from: '', to: '', tax: initial.value, isDefault: true}]);
                } else {
                    setTaxes([{from: '', to: '', tax: '', isDefault: true}]);
                }
            } catch (e) {
                setTaxes([{from: '', to: '', tax: initial.value, isDefault: true}]);
            }
        } else if (initial.id && (initial.id.startsWith('EmployerContribution_Tier_') || initial.id === 'EmployerContribution_Default')) {
            try {
                const parsed = JSON.parse(initial.value || '{}');
                setValue({ ...parsed, Rate: toDisplayDecimal(parsed.Rate) });
            } catch (e) {
                setValue({ FromYear: '', ToYear: '', Rate: '' });
            }
        } else {
            setValue(toDisplayDecimal(initial.value));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initial]);

    const handleTaxTableChange = (index, field, val) => {
        const newTaxes = [...taxes];
        newTaxes[index][field] = val;
        setTaxes(newTaxes);
    };

    const addTaxRow = () => {
        const newTaxes = [...taxes];
        const defaultIndex = newTaxes.findIndex(t => t.isDefault);
        const newRow = {from: '', to: '', tax: '', isDefault: false};
        
        if (defaultIndex !== -1) {
            newTaxes.splice(defaultIndex, 0, newRow);
        } else {
            newTaxes.push(newRow);
        }
        setTaxes(newTaxes);
    };
    
    const removeTaxRow = (index) => {
        setTaxes(taxes.filter((_, i) => i !== index));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        let newErrors = {};

        if (initial.id === 'EmploymentTax') {
            let taxErrors = {};
            let hasError = false;
            taxes.forEach((row, idx) => {
                if (!row.isDefault) {
                    if (row.from === '' || row.from === null || row.from === undefined) {
                        taxErrors[`from_${idx}`] = true;
                        hasError = true;
                    } else if (row.from.toString().length !== 4) {
                        taxErrors[`fromLength_${idx}`] = true;
                        hasError = true;
                    }
                    if (row.to === '' || row.to === null || row.to === undefined) {
                        taxErrors[`to_${idx}`] = true;
                        hasError = true;
                    } else if (row.to.toString().length !== 4) {
                        taxErrors[`toLength_${idx}`] = true;
                        hasError = true;
                    }
                }
                if (row.tax === '' || row.tax === null || row.tax === undefined) {
                    taxErrors[`tax_${idx}`] = true;
                    hasError = true;
                }
            });
            if (hasError) {
                setErrors(taxErrors);
                return;
            }
            setErrors({});
            onSave(initial.id, JSON.stringify(taxes));
        } else if (initial.id && initial.id.startsWith('EmployerContribution_Tier_')) {
            if (initial.id !== 'EmployerContribution_Default') {
                if (value.FromYear === '' || value.FromYear === null || value.FromYear === undefined) {
                    newErrors.FromYear = true;
                } else if (value.FromYear.toString().length !== 4) {
                    newErrors.FromYearLength = true;
                }
                if (value.ToYear === '' || value.ToYear === null || value.ToYear === undefined) {
                    newErrors.ToYear = true;
                } else if (value.ToYear.toString().length !== 4) {
                    newErrors.ToYearLength = true;
                }
            }
            if (value.Rate === '' || value.Rate === null || value.Rate === undefined) {
                newErrors.Rate = true;
            } else if (parseFloat(toApiDecimal(value.Rate)) > 100) {
                // Employer contribution is a percentage: it cannot exceed 100 %
                newErrors.RateMax = true;
            }

            if (Object.keys(newErrors).length > 0) {
                setErrors(newErrors);
                return;
            }
            setErrors({});

            if (settings) {
                const existingTiers = Object.keys(settings)
                    .filter(key => key.startsWith('EmployerContribution_Tier_') && key !== initial.id)
                    .map(key => {
                        try {
                            return JSON.parse(settings[key]);
                        } catch (e) {
                            return null;
                        }
                    })
                    .filter(t => t && t.FromYear && t.ToYear);

                const currentFrom = parseInt(value.FromYear, 10);
                const currentTo = parseInt(value.ToYear, 10);

                if (currentFrom > currentTo) {
                    FlashMessage(t('economic.invalidYearRange') || 'Från-år måste vara mindre än eller lika med Till-år', 'flash-messageError');
                    onCancel();
                    return;
                }

                const overlap = existingTiers.find(t => {
                    const from = parseInt(t.FromYear, 10);
                    const to = parseInt(t.ToYear, 10);
                    return currentFrom <= to && currentTo >= from;
                });

                if (overlap) {
                    FlashMessage(t('economic.overlappingYears', { from: overlap.FromYear, to: overlap.ToYear }) || `Årtalen överlappar med en befintlig inställning (${overlap.FromYear} - ${overlap.ToYear})`, 'flash-messageError');
                    onCancel();
                    return;
                }
            }
            onSave(initial.id, JSON.stringify({ ...value, Rate: Number(toApiDecimal(value.Rate)) }));
        } else if (initial.id === 'EmployerContribution_Default') {
            if (value.Rate === '' || value.Rate === null || value.Rate === undefined) {
                setErrors({ Rate: true });
                return;
            }
            if (parseFloat(toApiDecimal(value.Rate)) > 100) {
                setErrors({ RateMax: true });
                return;
            }
            setErrors({});
            onSave(initial.id, JSON.stringify({ ...value, Rate: Number(toApiDecimal(value.Rate)) }));
        } else {
            if (value === '' || value === null || value === undefined) {
                setErrors({ Value: true });
                return;
            }
            // Taxes are percentages: they cannot exceed 100 %
            if (initial.group === 'economic.skatter' && parseFloat(toApiDecimal(value)) > 100) {
                setErrors({ ValueMax: true });
                return;
            }
            // Mileage: the total ("Sammanlagd milersättning") can never be lower than its tax-free part
            // ("Varav skattefri del"), otherwise the taxable part becomes negative
            const newNumber = parseFloat(toApiDecimal(value));
            const taxFree = parseFloat(settings?.DeductableTravelPer10Km);
            const total = parseFloat(settings?.PayableTravelPer10Km);
            if (initial.id === 'PayableTravelPer10Km' && !isNaN(taxFree) && newNumber < taxFree) {
                setErrors({ MileageTotal: toDisplayDecimal(taxFree) });
                return;
            }
            if (initial.id === 'DeductableTravelPer10Km' && !isNaN(total) && newNumber > total) {
                setErrors({ MileageTaxFree: toDisplayDecimal(total) });
                return;
            }
            setErrors({});
            onSave(initial.id, toApiDecimal(value));
        }
    };

    const renderError = (show, messageKey) => show
        ? <div className="mt-1" style={{ color: '#f56e6e', fontSize: '12px' }}>{t(messageKey)}</div>
        : null;

    const handleYearChange = (field) => (e) => {
        const val = e.target.value;
        if (/^\d*$/.test(val)) {
            setValue({ ...value, [field]: val ? Number(val) : '' });
        }
    };

    return (
        <div className="select-org-panel-wrapper">
            {/* noValidate: errors are shown with the red messages below the fields, not browser tooltips */}
            <form onSubmit={handleSubmit} noValidate>
                {initial.id === 'EmploymentTax' ? (
                    <div className="mb-4">
                        <table className="table table-sm align-middle">
                            <thead>
                                <tr>
                                    <th className="select-org-panel-label fw-normal">{t('economic.birthYearFrom')}</th>
                                    <th className="select-org-panel-label fw-normal">{t('economic.birthYearTo')}</th>
                                    <th className="select-org-panel-label fw-normal">{t('economic.arbetsgivaravgift')}</th>
                                    <th style={{ width: '44px' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {taxes.map((row, idx) => (
                                    <tr key={idx}>
                                        <td>
                                            {!row.isDefault && (
                                                <>
                                                    <input
                                                        type="text"
                                                        maxLength={4}
                                                        inputMode="numeric"
                                                        className="form-control"
                                                        value={row.from ?? ''}
                                                        onChange={(e) => {
                                                            const val = e.target.value;
                                                            if (/^\d*$/.test(val)) {
                                                                handleTaxTableChange(idx, 'from', val ? Number(val) : '');
                                                            }
                                                        }}
                                                    />
                                                    {renderError(errors[`from_${idx}`], 'validationMessage.cannotBeEmpty')}
                                                    {renderError(errors[`fromLength_${idx}`], 'validationMessage.exact4Digits')}
                                                </>
                                            )}
                                        </td>
                                        <td>
                                            {!row.isDefault ? (
                                                <>
                                                    <input
                                                        type="text"
                                                        maxLength={4}
                                                        inputMode="numeric"
                                                        className="form-control"
                                                        value={row.to ?? ''}
                                                        onChange={(e) => {
                                                            const val = e.target.value;
                                                            if (/^\d*$/.test(val)) {
                                                                handleTaxTableChange(idx, 'to', val ? Number(val) : '');
                                                            }
                                                        }}
                                                    />
                                                    {renderError(errors[`to_${idx}`], 'validationMessage.cannotBeEmpty')}
                                                    {renderError(errors[`toLength_${idx}`], 'validationMessage.exact4Digits')}
                                                </>
                                            ) : (
                                                <span className="text-muted">{t('economic.defaultSetting')}</span>
                                            )}
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                step="0.01"
                                                className="form-control"
                                                value={row.tax ?? ''}
                                                onChange={(e) => handleTaxTableChange(idx, 'tax', e.target.value ? Number(e.target.value) : '')}
                                            />
                                            {renderError(errors[`tax_${idx}`], 'validationMessage.cannotBeEmpty')}
                                        </td>
                                        <td className="text-center">
                                            {!row.isDefault && (
                                                <Button className="box-red border-0" size="sm" onClick={() => removeTaxRow(idx)} type="button">
                                                    <i className="bx bx-x-circle fs-5 align-middle" />
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <Button className="bg-green-light text-green border-0" size="sm" onClick={addTaxRow} type="button">
                            <i className="bx bx-plus pe-1" />
                            {t('economic.addYearRow')}
                        </Button>
                    </div>
                ) : initial.id && (initial.id.startsWith('EmployerContribution_Tier_') || initial.id === 'EmployerContribution_Default') ? (
                    <>
                        {initial.id !== 'EmployerContribution_Default' && (
                            <div className="d-flex gap-3 mb-3">
                                <div className="flex-grow-1">
                                    <label htmlFor="economicFromYear" className="select-org-panel-label">{t('economic.birthYearFrom')}</label>
                                    <input
                                        id="economicFromYear"
                                        type="text"
                                        maxLength={4}
                                        inputMode="numeric"
                                        className="form-control"
                                        value={value?.FromYear ?? ''}
                                        onChange={handleYearChange('FromYear')}
                                    />
                                    {renderError(errors.FromYear, 'validationMessage.cannotBeEmpty')}
                                    {renderError(errors.FromYearLength, 'validationMessage.exact4Digits')}
                                </div>
                                <div className="flex-grow-1">
                                    <label htmlFor="economicToYear" className="select-org-panel-label">{t('economic.birthYearTo')}</label>
                                    <input
                                        id="economicToYear"
                                        type="text"
                                        maxLength={4}
                                        inputMode="numeric"
                                        className="form-control"
                                        value={value?.ToYear ?? ''}
                                        onChange={handleYearChange('ToYear')}
                                    />
                                    {renderError(errors.ToYear, 'validationMessage.cannotBeEmpty')}
                                    {renderError(errors.ToYearLength, 'validationMessage.exact4Digits')}
                                </div>
                            </div>
                        )}
                        <div className="mb-4">
                            <label htmlFor="economicRate" className="select-org-panel-label">{t('economic.arbetsgivaravgift')}</label>
                            <input
                                id="economicRate"
                                type="text"
                                inputMode="decimal"
                                className="form-control"
                                value={value?.Rate ?? ''}
                                onChange={(e) => {
                                    if (DECIMAL_INPUT.test(e.target.value)) setValue({ ...value, Rate: e.target.value });
                                }}
                            />
                            {renderError(errors.Rate, 'validationMessage.cannotBeEmpty')}
                            {renderError(errors.RateMax, 'economic.maxPercent')}
                        </div>
                    </>
                ) : (
                    <div className="mb-4">
                        <label htmlFor="economicValue" className="select-org-panel-label">{t(initial.title)}</label>
                        <input
                            id="economicValue"
                            type="text"
                            inputMode="decimal"
                            className="form-control"
                            value={value ?? ''}
                            onChange={(e) => {
                                if (DECIMAL_INPUT.test(e.target.value)) setValue(e.target.value);
                            }}
                        />
                        {renderError(errors.Value, 'validationMessage.cannotBeEmpty')}
                        {renderError(errors.ValueMax, 'economic.maxPercent')}
                        {errors.MileageTotal && (
                            <div className="mt-1" style={{ color: '#f56e6e', fontSize: '12px' }}>
                                {t('economic.mileageTotalBelowTaxFree', { value: errors.MileageTotal })}
                            </div>
                        )}
                        {errors.MileageTaxFree && (
                            <div className="mt-1" style={{ color: '#f56e6e', fontSize: '12px' }}>
                                {t('economic.mileageTaxFreeAboveTotal', { value: errors.MileageTaxFree })}
                            </div>
                        )}
                    </div>
                )}

                <div className="select-org-panel-buttons">
                    <button type="button" onClick={onCancel} disabled={saving} className="select-org-panel-btn-cancel">
                        {t('button.cancel') || 'Avbryt'}
                    </button>
                    <button
                        type="submit"
                        disabled={saving}
                        className={`select-org-panel-btn-confirm ${saving ? "disabled" : "enabled"}`}
                    >
                        {saving ? <Spinner size="sm" /> : (t('button.save') || 'Spara')}
                    </button>
                </div>
            </form>
        </div>
    );
};
